/**
 * PurusharthasCard — Five Purushartha pillars (Dharma · Artha · Kama · Moksha · Bhakti)
 * Rendered BELOW the existing Prayojana Vision content on each of the 4 tabs.
 *
 * Props:
 *   - tab: "awareness" | "alignment" | "awakening" | "harmony"
 *   - token: auth token (required for awareness — Yes/No/Partly answers persisted)
 *   - completedItems: array of completed item keys (for alignment/awakening/harmony tabs)
 *   - onTogglePipeline: (itemKey) => void  — wires into existing pipeline progress
 *
 * Item key convention (compatible with existing /dashboard/pipeline-progress flow):
 *   rel_{tab}_purusha_{pillarId}_{idx}
 *   e.g.  rel_alignment_purusha_dharma_0, rel_harmony_purusha_bhakti_3
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Compass, Loader2, Save, Check, CircleDot } from "lucide-react";
import { toast } from "sonner";
import { apiCall } from "../App";

const PILLAR_COLOR_CLASSES = {
  amber:   { dot: "bg-amber-400",   text: "text-amber-300",   border: "border-amber-400/30", bgSoft: "bg-amber-500/5",  bar: "bg-amber-400" },
  emerald: { dot: "bg-emerald-400", text: "text-emerald-300", border: "border-emerald-400/30", bgSoft: "bg-emerald-500/5", bar: "bg-emerald-400" },
  rose:    { dot: "bg-rose-400",    text: "text-rose-300",    border: "border-rose-400/30",  bgSoft: "bg-rose-500/5",   bar: "bg-rose-400" },
  violet:  { dot: "bg-violet-400",  text: "text-violet-300",  border: "border-violet-400/30", bgSoft: "bg-violet-500/5", bar: "bg-violet-400" },
  sky:     { dot: "bg-sky-400",     text: "text-sky-300",     border: "border-sky-400/30",   bgSoft: "bg-sky-500/5",    bar: "bg-sky-400" },
};

const TAB_HEADERS = {
  awareness: { title: "PURUSHARTHAS · 5 PILLARS — AWARENESS",
               subtitle: "Self-reflection across Dharma · Artha · Kama · Moksha · Bhakti. Yes / No / Partly + optional note." },
  alignment: { title: "PURUSHARTHAS · 5 PILLARS — ALIGNMENT",
               subtitle: "Daily, weekly, and monthly practices. Check items as you complete them — progress feeds your weekly chart." },
  awakening: { title: "PURUSHARTHAS · 5 PILLARS — AWAKENING",
               subtitle: "Core wisdom — check each statement once you have contemplated it. Progress feeds your weekly chart." },
  harmony:   { title: "PURUSHARTHAS · 5 PILLARS — HARMONY",
               subtitle: "Self-assessment checklist. Tick what you have integrated — progress feeds your weekly chart." },
};

// Maps a pillar+stage to the per-pillar item array
const STAGE_ITEMS_KEY = {
  alignment: "alignment_practices",
  awakening: "awakening_wisdom",
  harmony:   "harmony_checklist",
};

function itemKeyFor(tab, pillarId, idx) {
  return `rel_${tab}_purusha_${pillarId}_${idx}`;
}

// ───────── Awareness components ─────────

function ChoicePill({ label, selected, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-heading border transition-all ${
        selected
          ? "bg-divine-gold text-cosmic-void border-divine-gold"
          : "bg-cosmic-void/40 text-chakra-crown/70 border-divine-gold/20 hover:border-divine-gold/50"
      }`}
    >
      {label}
    </button>
  );
}

function AwarenessQuestion({ q, ans, onChange }) {
  const choice = ans?.choice || "";
  const note = ans?.note || "";
  return (
    <div className="rounded-xl border border-divine-gold/15 bg-cosmic-void/30 p-4 space-y-3" data-testid={`purusha-q-${q.id}`}>
      <p className="text-sm text-chakra-crown/90 leading-relaxed">{q.text}</p>
      <div className="flex flex-wrap gap-2">
        <ChoicePill label="Yes" selected={choice === "yes"}
          onClick={() => onChange({ choice: choice === "yes" ? "" : "yes", note })}
          testid={`purusha-choice-${q.id}-yes`} />
        <ChoicePill label="Partly" selected={choice === "partly"}
          onClick={() => onChange({ choice: choice === "partly" ? "" : "partly", note })}
          testid={`purusha-choice-${q.id}-partly`} />
        <ChoicePill label="No" selected={choice === "no"}
          onClick={() => onChange({ choice: choice === "no" ? "" : "no", note })}
          testid={`purusha-choice-${q.id}-no`} />
      </div>
      <textarea
        value={note}
        onChange={e => onChange({ choice, note: e.target.value.slice(0, 500) })}
        placeholder="Optional reflection (your own words)…"
        rows={2}
        maxLength={500}
        data-testid={`purusha-note-${q.id}`}
        className="w-full bg-cosmic-void/50 border border-divine-gold/15 rounded-lg p-2.5 text-chakra-crown text-xs leading-relaxed placeholder:text-chakra-crown/30 focus:outline-none focus:border-divine-gold/40"
      />
    </div>
  );
}

// ───────── Checkable reference item (alignment / awakening / harmony) ─────────

function CheckableItem({ itemKey, checked, onToggle, color, children, testid }) {
  const c = PILLAR_COLOR_CLASSES[color] || PILLAR_COLOR_CLASSES.amber;
  return (
    <li className={`flex items-start gap-3 text-sm rounded-lg p-3 border ${checked ? "bg-cosmic-void/50 border-divine-gold/25" : "bg-cosmic-void/40 border-divine-gold/10"}`}
        data-testid={testid}>
      {onToggle ? (
        <button onClick={() => onToggle(itemKey)}
          aria-label={checked ? "Mark incomplete" : "Mark complete"}
          data-testid={`${testid}-check`}
          className={`shrink-0 w-5 h-5 rounded border-[1.5px] flex items-center justify-center transition-all mt-0.5 ${
            checked ? `${c.bar} border-transparent` : "border-divine-gold/40 hover:border-divine-gold/70"
          }`}>
          {checked && <Check className="w-3 h-3 text-cosmic-void" strokeWidth={3} />}
        </button>
      ) : (
        <span className={`shrink-0 w-5 h-5 rounded border ${c.border} mt-0.5 flex items-center justify-center`}>
          <Check className={`w-3 h-3 ${c.text} opacity-30`} />
        </span>
      )}
      <div className="flex-1 leading-relaxed text-chakra-crown/85">
        {children}
      </div>
    </li>
  );
}

// ───────── Pillar Block ─────────

function PillarBlock({ pillar, tab, answers, onAnswerChange, expanded, onToggle, completedItems, onTogglePipeline }) {
  const c = PILLAR_COLOR_CLASSES[pillar.color] || PILLAR_COLOR_CLASSES.amber;

  // For alignment/awakening/harmony — compute per-pillar progress
  const stageKey = STAGE_ITEMS_KEY[tab];
  const items = stageKey ? (pillar[stageKey] || []) : [];
  const total = items.length;
  const doneCount = useMemo(() => {
    if (!stageKey || !completedItems?.length) return 0;
    return items.reduce((s, _, idx) => s + (completedItems.includes(itemKeyFor(tab, pillar.id, idx)) ? 1 : 0), 0);
  }, [completedItems, items, tab, pillar.id, stageKey]);

  // For awareness — compute answered count
  const awarenessAnswered = useMemo(() => {
    if (tab !== "awareness") return 0;
    return (pillar.awareness_questions || []).filter(q => answers[q.id]?.choice).length;
  }, [answers, pillar.awareness_questions, tab]);
  const awarenessTotal = pillar.awareness_questions?.length || 0;

  const progressDone = tab === "awareness" ? awarenessAnswered : doneCount;
  const progressTotal = tab === "awareness" ? awarenessTotal : total;
  const progressPct = progressTotal > 0 ? Math.round((progressDone / progressTotal) * 100) : 0;

  return (
    <div className={`rounded-2xl border ${c.border} ${c.bgSoft} overflow-hidden transition-colors`} data-testid={`purusha-pillar-${pillar.id}`}>
      {/* Header — clickable to toggle */}
      <button
        type="button"
        onClick={onToggle}
        data-testid={`purusha-toggle-${pillar.id}`}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-cosmic-void/30 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-2.5 h-2.5 rounded-full ${c.dot} shadow-[0_0_8px] shadow-current ${c.text} shrink-0`} />
          <div className="text-left min-w-0">
            <h4 className={`font-heading text-base tracking-wide ${c.text}`}>{pillar.name.toUpperCase()}</h4>
            <p className="text-[11px] text-chakra-crown/55 mt-0.5 truncate">{pillar.tagline}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[10px] text-chakra-crown/55 font-heading tracking-wide whitespace-nowrap" data-testid={`purusha-progress-${pillar.id}`}>
            {progressDone}/{progressTotal}
          </span>
          <div className="w-14 h-1 bg-cosmic-void/60 rounded-full overflow-hidden">
            <div className={`h-full ${c.bar} transition-all`} style={{ width: `${progressPct}%` }} />
          </div>
          <ChevronDown className={`w-4 h-4 text-chakra-crown/60 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>
      </button>

      {/* Body */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 space-y-3">
              {tab === "awareness" && (
                <div className="space-y-3">
                  {pillar.awareness_questions.map(q => (
                    <AwarenessQuestion
                      key={q.id}
                      q={q}
                      ans={answers[q.id]}
                      onChange={a => onAnswerChange(q.id, a)}
                    />
                  ))}
                </div>
              )}

              {tab === "alignment" && (
                <ul className="space-y-2" data-testid={`purusha-alignment-${pillar.id}`}>
                  {items.map((p, i) => {
                    const k = itemKeyFor(tab, pillar.id, i);
                    return (
                      <CheckableItem key={i} itemKey={k} checked={completedItems?.includes(k)}
                        onToggle={onTogglePipeline} color={pillar.color}
                        testid={`purusha-item-${pillar.id}-${i}`}>
                        <div className="flex items-start gap-2 flex-wrap">
                          <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-heading uppercase tracking-wider ${c.text} ${c.bgSoft} border ${c.border}`}>
                            {p.cadence}
                          </span>
                          <span>{p.action}</span>
                        </div>
                      </CheckableItem>
                    );
                  })}
                </ul>
              )}

              {tab === "awakening" && (
                <ul className="space-y-2" data-testid={`purusha-awakening-${pillar.id}`}>
                  {items.map((w, i) => {
                    const k = itemKeyFor(tab, pillar.id, i);
                    return (
                      <CheckableItem key={i} itemKey={k} checked={completedItems?.includes(k)}
                        onToggle={onTogglePipeline} color={pillar.color}
                        testid={`purusha-item-${pillar.id}-${i}`}>
                        <span className="flex items-start gap-2">
                          <CircleDot className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${c.text}`} />
                          <span className="italic">{w}</span>
                        </span>
                      </CheckableItem>
                    );
                  })}
                </ul>
              )}

              {tab === "harmony" && (
                <ul className="space-y-1.5" data-testid={`purusha-harmony-${pillar.id}`}>
                  {items.map((item, i) => {
                    const k = itemKeyFor(tab, pillar.id, i);
                    return (
                      <CheckableItem key={i} itemKey={k} checked={completedItems?.includes(k)}
                        onToggle={onTogglePipeline} color={pillar.color}
                        testid={`purusha-item-${pillar.id}-${i}`}>
                        {item}
                      </CheckableItem>
                    );
                  })}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ───────── Awareness summary (rendered at TOP of Awareness tab) ─────────

function AwarenessSummary({ pillars, answers }) {
  // Per-pillar Yes/Partly/No counts
  const rows = pillars.map(p => {
    const counts = { yes: 0, partly: 0, no: 0, blank: 0 };
    p.awareness_questions.forEach(q => {
      const c = answers[q.id]?.choice;
      if (c === "yes") counts.yes++;
      else if (c === "partly") counts.partly++;
      else if (c === "no") counts.no++;
      else counts.blank++;
    });
    return { pillar: p, counts, total: p.awareness_questions.length };
  });
  const anyAnswered = rows.some(r => r.counts.yes + r.counts.partly + r.counts.no > 0);
  if (!anyAnswered) return null;
  return (
    <div className="rounded-xl border border-divine-gold/15 bg-cosmic-deep/40 p-4 mb-4" data-testid="purusha-awareness-summary">
      <p className="label-sacred text-xs mb-3">PURUSHARTHA INTEGRATION · YOUR SNAPSHOT</p>
      <div className="space-y-2.5">
        {rows.map(({ pillar, counts, total }) => {
          const cc = PILLAR_COLOR_CLASSES[pillar.color] || PILLAR_COLOR_CLASSES.amber;
          const answered = counts.yes + counts.partly + counts.no;
          const yesPct = total > 0 ? (counts.yes / total) * 100 : 0;
          const partlyPct = total > 0 ? (counts.partly / total) * 100 : 0;
          const noPct = total > 0 ? (counts.no / total) * 100 : 0;
          return (
            <div key={pillar.id} className="flex items-center gap-3" data-testid={`purusha-summary-row-${pillar.id}`}>
              <span className={`w-2 h-2 rounded-full ${cc.dot} shrink-0`} />
              <span className={`text-xs font-heading tracking-wide w-20 shrink-0 ${cc.text}`}>{pillar.name}</span>
              <div className="flex-1 h-2 bg-cosmic-void/70 rounded-full overflow-hidden flex">
                <div className="bg-emerald-400 h-full" style={{ width: `${yesPct}%` }} />
                <div className="bg-amber-400 h-full" style={{ width: `${partlyPct}%` }} />
                <div className="bg-rose-400 h-full" style={{ width: `${noPct}%` }} />
              </div>
              <span className="text-[10px] text-chakra-crown/55 w-10 text-right">{answered}/{total}</span>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3 text-[10px] text-chakra-crown/45">
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-400 rounded-full" /> Yes</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-full" /> Partly</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-rose-400 rounded-full" /> No</span>
      </div>
    </div>
  );
}

// ───────── Main Component ─────────

export default function PurusharthasCard({ tab = "awareness", token, completedItems, onTogglePipeline }) {
  const [spec, setSpec] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState({}); // pillar.id -> bool — closed by default for ALL

  // Load spec + answers
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const specRes = await apiCall("GET", "/purusharthas/spec", null, token);
      let ansRes = { success: true, data: { answers: {} } };
      if (tab === "awareness") {
        ansRes = await apiCall("GET", "/purusharthas/me", null, token);
      }
      if (cancelled) return;
      if (specRes.success) setSpec(specRes.data);
      if (ansRes.success) setAnswers(ansRes.data?.answers || {});
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [tab, token]);

  const header = TAB_HEADERS[tab] || TAB_HEADERS.awareness;

  const answeredCount = useMemo(() => {
    if (!spec || tab !== "awareness") return 0;
    const allQids = spec.pillars.flatMap(p => p.awareness_questions.map(q => q.id));
    return allQids.filter(qid => answers[qid]?.choice).length;
  }, [spec, answers, tab]);
  const totalQuestions = useMemo(() => {
    if (!spec) return 0;
    return spec.pillars.reduce((s, p) => s + p.awareness_questions.length, 0);
  }, [spec]);

  const handleAnswerChange = (qid, entry) => {
    setAnswers(a => ({ ...a, [qid]: entry }));
    setDirty(true);
  };

  const saveAnswers = async () => {
    setSaving(true);
    const r = await apiCall("PUT", "/purusharthas/me", { answers }, token);
    setSaving(false);
    if (r.success) {
      toast.success(`Saved · ${r.data.saved} answer${r.data.saved === 1 ? "" : "s"}`);
      setDirty(false);
    } else {
      toast.error(r.error || "Save failed");
    }
  };

  const togglePillar = (pid) =>
    setExpanded(e => ({ ...e, [pid]: !e[pid] }));

  const expandAll = () => {
    if (!spec) return;
    setExpanded(Object.fromEntries(spec.pillars.map(p => [p.id, true])));
  };
  const collapseAll = () => setExpanded({});

  if (loading) {
    return (
      <div className="sacred-card flex items-center justify-center py-6 text-chakra-crown/50" data-testid="purusha-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading Purusharthas…
      </div>
    );
  }
  if (!spec || !spec.pillars?.length) return null;

  return (
    <div className="sacred-card" data-testid={`purusharthas-card-${tab}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-divine-gold/10 flex items-center justify-center shrink-0">
            <Compass className="w-5 h-5 text-divine-gold" />
          </div>
          <div>
            <h3 className="font-heading text-base text-divine-gold tracking-wide">{header.title}</h3>
            <p className="text-xs text-chakra-crown/55 mt-1 leading-relaxed">{header.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={expandAll}
            className="text-[11px] text-chakra-crown/55 hover:text-divine-gold transition-colors px-2 py-1 rounded border border-divine-gold/15 hover:border-divine-gold/40"
            data-testid="purusha-expand-all">Expand all</button>
          <button onClick={collapseAll}
            className="text-[11px] text-chakra-crown/55 hover:text-divine-gold transition-colors px-2 py-1 rounded border border-divine-gold/15 hover:border-divine-gold/40"
            data-testid="purusha-collapse-all">Collapse</button>
        </div>
      </div>

      {/* Awareness Snapshot (result reflection) */}
      {tab === "awareness" && <AwarenessSummary pillars={spec.pillars} answers={answers} />}

      {/* Awareness progress + save bar */}
      {tab === "awareness" && (
        <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg bg-cosmic-void/40 border border-divine-gold/10">
          <span className="text-xs text-chakra-crown/60">
            <span className="font-heading text-divine-gold">{answeredCount}</span>
            <span className="text-chakra-crown/40"> / {totalQuestions} reflections answered</span>
          </span>
          <button onClick={saveAnswers}
            disabled={saving || !dirty}
            data-testid="purusha-save-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-divine-gold text-cosmic-void font-heading text-xs hover:bg-divine-gold-light disabled:opacity-40 transition-all">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {dirty ? "Save Reflections" : "Saved"}
          </button>
        </div>
      )}

      {/* Pillars */}
      <div className="space-y-2.5">
        {spec.pillars.map(p => (
          <PillarBlock
            key={p.id}
            pillar={p}
            tab={tab}
            answers={answers}
            onAnswerChange={handleAnswerChange}
            expanded={!!expanded[p.id]}
            onToggle={() => togglePillar(p.id)}
            completedItems={completedItems}
            onTogglePipeline={onTogglePipeline}
          />
        ))}
      </div>

      {/* Footer note */}
      <p className="text-[10px] text-chakra-crown/35 italic pt-3 leading-relaxed">
        Drawn from <em>The Five Purusharthas — A Practitioner&apos;s Guide</em>. Each pillar appears on every tab:
        Awareness · Alignment · Awakening · Harmony — preserving your existing flow.
      </p>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// PurusharthasAwarenessPair — Awareness-tab 2-column layout
// LEFT: question-side card (5 pillar accordions + save)
// RIGHT: result snapshot panel (tri-colour bars), matches existing CollapsibleResult pattern
// ════════════════════════════════════════════════════════════════════════════

export function PurusharthasAwarenessPair({ token }) {
  const [spec, setSpec] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState({}); // pillar.id -> bool — closed by default
  const [resultExpanded, setResultExpanded] = useState(true); // right-pane CollapsibleResult-like toggle

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [specRes, ansRes] = await Promise.all([
        apiCall("GET", "/purusharthas/spec", null, token),
        apiCall("GET", "/purusharthas/me", null, token),
      ]);
      if (cancelled) return;
      if (specRes.success) setSpec(specRes.data);
      if (ansRes.success) setAnswers(ansRes.data?.answers || {});
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const answeredCount = useMemo(() => {
    if (!spec) return 0;
    const allQids = spec.pillars.flatMap(p => p.awareness_questions.map(q => q.id));
    return allQids.filter(qid => answers[qid]?.choice).length;
  }, [spec, answers]);
  const totalQuestions = useMemo(() => {
    if (!spec) return 0;
    return spec.pillars.reduce((s, p) => s + p.awareness_questions.length, 0);
  }, [spec]);

  const handleAnswerChange = (qid, entry) => {
    setAnswers(a => ({ ...a, [qid]: entry }));
    setDirty(true);
  };
  const saveAnswers = async () => {
    setSaving(true);
    const r = await apiCall("PUT", "/purusharthas/me", { answers }, token);
    setSaving(false);
    if (r.success) {
      toast.success(`Saved · ${r.data.saved} answer${r.data.saved === 1 ? "" : "s"}`);
      setDirty(false);
    } else { toast.error(r.error || "Save failed"); }
  };
  const togglePillar = (pid) => setExpanded(e => ({ ...e, [pid]: !e[pid] }));
  const expandAll = () => spec && setExpanded(Object.fromEntries(spec.pillars.map(p => [p.id, true])));
  const collapseAll = () => setExpanded({});

  if (loading) {
    return (
      <div className="sacred-card flex items-center justify-center py-6 text-chakra-crown/50" data-testid="purusha-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading Purusharthas…
      </div>
    );
  }
  if (!spec || !spec.pillars?.length) return null;

  const anyAnswered = answeredCount > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch" data-testid="rel-paired-row-purushartha">
      {/* ═══════════ LEFT: Question-side card ═══════════ */}
      <div className="p-4 rounded-xl border bg-cosmic-light/5 border-divine-gold/10" data-testid="rel-test-card-purushartha">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-divine-gold/10">
            <Compass className="w-5 h-5 text-divine-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-heading text-divine-gold tracking-wide">PURUSHARTHAS · 5 PILLARS</p>
            <p className="text-xs text-chakra-crown/40 font-heading">Dharma · Artha · Kama · Moksha · Bhakti</p>
            <p className="text-xs text-chakra-crown/50 mt-1.5 leading-relaxed">
              Self-reflection across the five Purusharthas — Yes / No / Partly with optional note.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className="text-[10px] text-chakra-crown/50 font-heading">{totalQuestions} questions</span>
            <div className="flex gap-1.5">
              <button onClick={expandAll} className="text-[10px] text-chakra-crown/55 hover:text-divine-gold px-2 py-0.5 rounded border border-divine-gold/15 hover:border-divine-gold/40 transition-colors" data-testid="purusha-expand-all">Expand all</button>
              <button onClick={collapseAll} className="text-[10px] text-chakra-crown/55 hover:text-divine-gold px-2 py-0.5 rounded border border-divine-gold/15 hover:border-divine-gold/40 transition-colors" data-testid="purusha-collapse-all">Collapse</button>
            </div>
          </div>
        </div>

        {/* Save / progress bar */}
        <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg bg-cosmic-void/40 border border-divine-gold/10">
          <span className="text-xs text-chakra-crown/60">
            <span className="font-heading text-divine-gold">{answeredCount}</span>
            <span className="text-chakra-crown/40"> / {totalQuestions} reflections</span>
          </span>
          <button onClick={saveAnswers} disabled={saving || !dirty}
            data-testid="purusha-save-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-divine-gold text-cosmic-void font-heading text-xs hover:bg-divine-gold-light disabled:opacity-40 transition-all">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {dirty ? "Save Reflections" : "Saved"}
          </button>
        </div>

        {/* 5 Pillars — each expandable/collapsible with chevron */}
        <div className="space-y-2.5">
          {spec.pillars.map(p => (
            <PillarBlock
              key={p.id}
              pillar={p}
              tab="awareness"
              answers={answers}
              onAnswerChange={handleAnswerChange}
              expanded={!!expanded[p.id]}
              onToggle={() => togglePillar(p.id)}
              completedItems={[]}
              onTogglePipeline={null}
            />
          ))}
        </div>
        <p className="text-[10px] text-chakra-crown/35 italic pt-3 leading-relaxed">
          Drawn from <em>The Five Purusharthas — A Practitioner&apos;s Guide</em>.
        </p>
      </div>

      {/* ═══════════ RIGHT: Result snapshot card (CollapsibleResult-style) ═══════════ */}
      <div className="p-4 rounded-xl border bg-cosmic-light/5 border-divine-gold/10" data-testid="rel-result-card-purushartha">
        <button
          type="button"
          onClick={() => setResultExpanded(v => !v)}
          className="w-full flex items-start justify-between gap-3 mb-3"
          data-testid="purusha-result-toggle"
        >
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-divine-gold/10">
              <Compass className="w-5 h-5 text-divine-gold" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-sm font-heading text-divine-gold tracking-wide">PURUSHARTHAS · YOUR SNAPSHOT</p>
              <p className="text-xs text-chakra-crown/50 mt-1 leading-relaxed">Yes / Partly / No distribution across the five pillars</p>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-chakra-crown/60 transition-transform mt-1 shrink-0 ${resultExpanded ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence initial={false}>
          {resultExpanded && (
            <motion.div
              key="snapshot-body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="overflow-hidden"
            >
              {!anyAnswered ? (
                <p className="text-chakra-crown/50 text-sm text-center py-4" data-testid="purusha-snapshot-empty">
                  Answer at least one reflection on the left to see your Purushartha integration snapshot.
                </p>
              ) : (
                <div data-testid="purusha-awareness-summary" className="pt-1">
                  <p className="label-sacred text-xs mb-3">PURUSHARTHA INTEGRATION · YOUR SNAPSHOT</p>
                  <div className="space-y-2.5">
                    {spec.pillars.map(p => {
                      const cc = PILLAR_COLOR_CLASSES[p.color] || PILLAR_COLOR_CLASSES.amber;
                      const counts = { yes: 0, partly: 0, no: 0 };
                      p.awareness_questions.forEach(q => {
                        const c = answers[q.id]?.choice;
                        if (c === "yes") counts.yes++;
                        else if (c === "partly") counts.partly++;
                        else if (c === "no") counts.no++;
                      });
                      const answered = counts.yes + counts.partly + counts.no;
                      const total = p.awareness_questions.length;
                      const yesPct = (counts.yes / total) * 100;
                      const partlyPct = (counts.partly / total) * 100;
                      const noPct = (counts.no / total) * 100;
                      return (
                        <div key={p.id} className="space-y-1" data-testid={`purusha-summary-row-${p.id}`}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${cc.dot}`} />
                              <span className={`font-heading tracking-wide ${cc.text}`}>{p.name}</span>
                            </span>
                            <span className="text-chakra-crown/55">{answered}/{total}</span>
                          </div>
                          <div className="h-2 bg-cosmic-void/70 rounded-full overflow-hidden flex">
                            <div className="bg-emerald-400 h-full transition-all" style={{ width: `${yesPct}%` }} />
                            <div className="bg-amber-400 h-full transition-all" style={{ width: `${partlyPct}%` }} />
                            <div className="bg-rose-400 h-full transition-all" style={{ width: `${noPct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-divine-gold/10 text-[10px] text-chakra-crown/45">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-400 rounded-full" /> Yes</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-full" /> Partly</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-rose-400 rounded-full" /> No</span>
                  </div>
                  <p className="text-[10px] text-chakra-crown/40 italic pt-2 leading-relaxed">
                    Revisit weekly. Strong Yes-bars on multiple pillars signal integration; Partly bars are growth-edges; No bars are invitation pillars.
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
